import { useState, useEffect } from 'react';
import { Text, Flex, Box, AspectRatio } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next';
import MenuListItem from './MenuListItem';
import { connect } from 'react-redux';
import { extractDescriptionFromCategory, extractFootnoteFromCategory, extractDescriptionFromMenuItem, extractNameFromCategory, extractNameFromMenuItem } from '../../utils/ExtractInternationalizedKeyFromCategory';
import { categoriesToHashTable } from '../../utils/Categories Graph/CategoriesArrayToHashTable';
import { dishesInCategoryToHashTable } from '../../utils/Dishes Graph/DishesArrayToHashTable';
import { collectPaths } from '../../utils/DirectedGraph+Traversal';
import { Hour, extractOpenHoursFromCategory } from '../../utils/ExtractOpenHoursFromCategory';

const mapStateToProps = (state) => {
    const categoriesLU = categoriesToHashTable(state.categories)

    return {
        itemsByCategories: state.itemsByCategories,
        categoriesLU: categoriesLU,
        categoriesGraph: state.categoriesGraph,
        menuGraph: state.dishesByCategoriesGraphLookup, 
        restaurant: state.restaurantInfo
    }
}

const MenuCategory = connect(mapStateToProps)(
    (props) => {
        const [categoryDescription, setCategoryDescription] = useState(null) 
        const [categoryFootnote, setCategoryFootnote] = useState(null)
        const [categoryName, setCategoryName] = useState(null)
        const [categoryPDF, setCategoryPDF] = useState(null)
        const [subcategoriesList, setSubcategoriesList] = useState(null)
        const [itemsGraph, setItemsGraph] = useState(null)
        const [dishesLookup, setDishesLookup] = useState(null)
        const [isCategoryOpen, setCategoryOpen] = useState(null)
        const [hasOpenHours, setHasOpenHours] = useState(null)

        const { t } = useTranslation()

        // Build the graph of all the dishes for this category
        useEffect(() => {
            if (!!props.itemsByCategories && !!props.categoryKey && !!props.menuGraph) {
                let dishesLookup = dishesInCategoryToHashTable(/*dishesInThisCategory:*/ props.itemsByCategories[props.categoryKey])
                let dishesGraph = props.menuGraph[props.categoryKey]

                collectPaths(
                    dishesGraph, 'root', null, null
                )
            
                setItemsGraph(dishesGraph)
                setDishesLookup(dishesLookup)
            }
        }, [props.itemsByCategories, props.menuGraph, props.categoryKey])

        useEffect(() => {
            if (props.categoriesLU != null && props.restaurant != null && props.categoryKey != null) {
                const currentHour = new Date().getHours();
                const currentMinute = new Date().getMinutes();
                const currentTime = new Hour(currentHour, currentMinute);
    
                setCategoryOpen(extractOpenHoursFromCategory(props.categoriesLU[props.categoryKey], props.restaurant)?.isOpen(currentTime))
                setHasOpenHours(!!props.restaurant.openHours || !!props.categoriesLU[props.categoryKey].openHours)
            }
        }, [props.categoriesLU, props.restaurant, props.categoryKey])

        // Drill name from props
        useEffect(() => {
            if (!!props.categoriesLU) {
                const thisCategory = props.categoriesLU[props.categoryKey]

                if (!!props.categoryKey && !!thisCategory) {
                    setCategoryDescription(extractDescriptionFromCategory(thisCategory))
                    setCategoryName(extractNameFromCategory(thisCategory))
                    setCategoryFootnote(extractFootnoteFromCategory(thisCategory))
                    setCategoryPDF(thisCategory.categoryPdf)
                }
            }
        }, [props.categoryKey, props.categoriesLU]) 

        
        // List the subcategories of the current category (next level in the graph) as an array
        useEffect(() => {
            if (!!props.categoriesGraph) {
                const subcategories = props
                        .categoriesGraph
                        .outgoingEdgesOf(props.categoryKey)
                        .map((edge) => edge.dest)
                        .sort((a, b) => {
                            return props.categoriesLU[a].pos - props.categoriesLU[b].pos
                        })    
                    
                if (subcategories.length <= 0) {
                    setSubcategoriesList([])
                } else {
                    if (!subcategoriesList || subcategories.length !== subcategoriesList.length) {
                        setSubcategoriesList(subcategories)
                    } else {
                        var shouldUpdate = true  
                        for (var i=0; i < shouldUpdate && subcategories.length; i++) {
                            shouldUpdate = shouldUpdate && (subcategories[i] === subcategoriesList[i])
                        }
    
                        if (shouldUpdate) {
                            setSubcategoriesList(subcategories)
                        }
                    }
                }
            }
        }, [props.categoriesGraph, props.categoryKey])


        const menu = (!!itemsGraph && !!dishesLookup) && 
            itemsGraph
                .outgoingEdgesOf('root')
                ?.sort((edgeA, edgeB) => dishesLookup[edgeA.dest].pos - dishesLookup[edgeB.dest].pos)
                .map(
                    edge => {
                        let item = dishesLookup[edge.dest]

                        return (
                            <MenuListItem   ID={ item.itemKey }
                                            categoryID={ props.categoryKey }
                                            key={ item.itemKey }
                                            isTopLevel
                                            name={extractNameFromMenuItem(item)}
                                            description={extractDescriptionFromMenuItem(item)}
                                            renderRecursive={true}
                                            includeImage={props.itemsByCategories[props.categoryKey].reduce((accumulator, currentValue) => {
                                                return accumulator ||  currentValue.image !== null 
                                            }, false)}
                                        />
                        );
                    }
        )

        const scaledNameSizes = ["lg", "lg", "sm"]
        const scaledDescriptionSizes = ["regular", "small", "smaller"]

        return (
            (!!subcategoriesList) &&
            <>
                <Flex ml={2 * Math.max(0, props.depth ?? -1) - 1 } mr={2*Math.min(1, (props.depth ?? 0) + 2)} direction="column" mt={(props.depth ?? -1) > 0 ? 4 : 0}>

                    <Flex width="100%" mt="2" pl={2}>
                        <Text 
                            fontWeight="bold" 
                            fontSize={(props.depth ?? -1) < 0 ? "xl" : (props.depth ?? 0) <= 2 ? scaledNameSizes[props.depth ?? 0] : "sm"} 
                            color="green.700"
                        >
                            {categoryName}
                        </Text>

                        {
                        // OPEN HOURS
                        (props.showIsOpen && hasOpenHours) &&
                            (isCategoryOpen ?
                                <Text fontSize="normal" fontWeight="medium" color="#4BAC61" className="open_hours_title"  display="inline-block" ml="auto" mr={3} >
                                {   t("open")}
                                </Text> :
                                <Text fontSize="normal" fontWeight="medium" color="#680000" className="open_hours_title"  display="inline-block" ml="auto" mr={3} >  
                                    {t("closed")}
                                </Text>)
                        }
                    </Flex>


                    {   categoryDescription &&
                            <Box width="100%" pl="2" color="lightblack" mb="2" mt="1">
                                {
                                    categoryDescription.map(
                                        (descriptionSlice, index) => {
                                            return (
                                                <Text 
                                                    fontWeight="normal" 
                                                    fontSize={(props.depth ?? -1) < 0 ? "medium" : (props.depth ?? 0) <= 2 ? scaledDescriptionSizes[props.depth ?? 0] : "smaller"} 
                                                    color="catdescriptiongrey" 
                                                    lineHeight="1.2" 
                                                    key={descriptionSlice} 
                                                    mb={index < categoryDescription.length - 1 ? 2 : 0}
                                                >
                                                    {descriptionSlice}
                                                </Text>
                                            )
                                        }
                                    )
                                } 
                            </Box>
                    }

                    { !!categoryPDF ?
                        <AspectRatio ratio={0.5} width="100%" mt={4}>
                            <iframe title={props.categoryKey} src={categoryPDF} style={{border: '0px'}} autoPlay={true}/>
                        </AspectRatio> : menu
                    }

                    { !!categoryFootnote &&
                    <Flex direction="column" width="100%" mt={6}>
                            {
                                categoryFootnote.map(footnoteSlice =>
                                    <Text color="blackAlpha.700" fontWeight="medium" fontSize="xs" ml={2} mt={1}>
                                        { footnoteSlice }
                                    </Text>
                                )
                            }
                    </Flex>
                    }

                    { (props.renderRecursive && !!subcategoriesList) &&
                        subcategoriesList.map((subcategoryKey) => {
                            return <MenuCategory 
                                    key={subcategoryKey} 
                                    categoryKey={subcategoryKey} 
                                    depth={(props.depth ?? -1) + 1}
                                    showIsOpen={false}
                                    renderRecursive/>
                        })
                    }
                </Flex>
            </>
        )
    }
)

export default MenuCategory